import React, { useState, useEffect } from "react";
import {
  CreditCardIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useLoading } from "../contexts/LoadingContext";
import { toast } from "react-hot-toast";
import {
  getPortalLink,
  upgradePlan,
  cancelSubscription,
  reactivateSubscription,
  getSubscriptionDetails,
  createSession,
} from "../api/user";
import UpgradeConfirmationModal from "../components/UpgradeConfirmationModal";
import ContactSaleModal from "../components/ContactSaleModal";
import PLANS from "../utils/plansv2";
import api from "../utils/axios";
import {
  SubscriptionOverviewTab,
  SubscriptionUsageTab,
  SubscriptionUpgradeTab,
  SubscriptionBillingTab,
  SubscriptionActivityTab,
} from "../components/subscription";

const SUB_TABS = {
  overview: "overview",
  usage: "usage",
  upgrade: "upgrade",
  billing: "billing",
  activity: "activity",
};

const SubscriptionAndUsage = () => {
  const plans = PLANS;
  const { user, subscription, organization, getProfile } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState(SUB_TABS.overview);

  const isOrgOwner =
    !!organization &&
    organization.ownerId?.toString() === (user?._id || user?.id)?.toString();
  const { showLoader, hideLoader, isLoading } = useLoading();
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showContactSaleModal, setShowContactSaleModal] = useState(false);
  const [selectedPlanForContactSale, setSelectedPlanForContactSale] =
    useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch payment method + transaction history when org owner has subscription
  useEffect(() => {
    if (
      isOrgOwner &&
      organization?.isSubscribed &&
      (organization?.stripeSubscriptionId ||
        organization?.razorpaySubscriptionId)
    ) {
      setLoadingDetails(true);
      getSubscriptionDetails()
        .then((res) => {
          if (res?.paymentMethod !== undefined || res?.invoices) {
            setSubscriptionDetails(res);
          }
        })
        .catch(() => setSubscriptionDetails(null))
        .finally(() => setLoadingDetails(false));
    } else {
      setSubscriptionDetails(null);
    }
  }, [
    isOrgOwner,
    organization?.isSubscribed,
    organization?.stripeSubscriptionId,
    organization?.razorpaySubscriptionId,
  ]);

  // Get payment country from organization, default to US if not set
  const paymentCountry = organization?.payment_country || "US";
  const isINR = paymentCountry === "IN";
  const currency = isINR ? "INR" : "USD";
  const currencySymbol = isINR ? "₹" : "$";

  // Fetch available plans for upgrade
  useEffect(() => {
    if (organization?.planId) {
      fetchAvailablePlans();
    }
  }, [organization?.planId]);

  const fetchAvailablePlans = () => {
    try {
      setLoadingPlans(true);

      // Filter to show only Professional plan for Standard users (no downgrade allowed)
      let availablePlans = [];
      const currentPlanIndex = plans.findIndex(
        (plan) =>
          plan.id === organization?.planId ||
          plan.id === organization?.planId?.replace("_IN", ""),
      );
      availablePlans = plans.filter(
        (plan, idx) => plan.id !== "FREE" && idx > currentPlanIndex,
      );

      // Filter to show only plans with matching billing cycle
      // const userBillingCycle = user?.company?.subscriptionType?.toLowerCase();
      // if (userBillingCycle) {
      //   availablePlans = availablePlans.filter((plan) => {
      //     // Check if the plan has the same billing cycle as user's current plan
      //     const planHasMatchingBilling =
      //       (userBillingCycle === "monthly" && plan.monthlyPrice) ||
      //       (userBillingCycle === "yearly" && plan.yearlyPrice);

      //     return planHasMatchingBilling;
      //   });
      // }

      console.log(availablePlans, "availablePlans");

      setAvailablePlans(availablePlans);
    } catch (error) {
      console.error("Error loading plans:", error);
      toast.error("Failed to load upgrade options");
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleUpgradeClick = (plan, interval) => {
    setSelectedPlan({ plan, interval });
    setShowConfirmationModal(true);
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const proceedRazorpaySubscription = async (subscriptionId, razorpayKeyId) => {
    const res = await loadRazorpay();
    if (!res) {
      toast.error("Razorpay SDK failed to load");
      return;
    }
    hideLoader();
    const options = {
      key: razorpayKeyId,
      subscription_id: subscriptionId,
      name: "EzSignly",
      description: "EzSignly Subscription",
      modal: { ondismiss: () => hideLoader() },
      handler: async () => {
        await api.post("/stripe/razorpay/set-payment");
        hideLoader();
        window.location.reload();
      },
      prefill: { name: user?.name, email: user?.email },
      theme: { color: "#1A6EDB" },
      config: {
        display: {
          blocks: {
            banks: {
              name: "Pay using",
              instruments: [
                { method: "upi" },
                { method: "card" },
                { method: "wallet" },
              ],
            },
          },
          sequence: ["block.banks"],
          preferences: { show_default_blocks: false },
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handlePlanSelection = async (planId, subscriptionType) => {
    try {
      showLoader();
      const response = await createSession({
        planId,
        subscriptionType: subscriptionType.toUpperCase(),
        memberCount: 1,
      });

      if (response.url) {
        window.location.href = response.url;
      } else if (
        response.paymentGateway === "RAZORPAY" &&
        response.subscriptionId
      ) {
        proceedRazorpaySubscription(
          response.subscriptionId,
          response.razorpayKeyId,
        );
      } else {
        hideLoader();
        window.location.reload();
      }
    } catch (error) {
      console.error("Error selecting plan:", error);
      hideLoader();
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to create checkout session",
      );
    }
  };

  const handleUpgradeConfirm = async () => {
    try {
      showLoader();
      setShowConfirmationModal(false);

      const { plan, interval } = selectedPlan;
      console.log(plan, interval, "plan, interval");
      const planId = plan.id;

      if (organization?.planId === "FREE") {
        console.log("free plan");
        handlePlanSelection(planId, interval.toUpperCase());
        return;
      }

      const response = await upgradePlan(planId, interval.toUpperCase());

      if (response) {
        toast.success("Plan upgraded successfully!");
        // Refresh the page to show updated subscription info
        window.location.reload();
      } else {
        toast.error(response.message || "Failed to upgrade plan");
      }
    } catch (error) {
      console.error("Error upgrading plan:", error);

      // Handle different types of errors
      if (error.message) {
        toast.error(error.message);
      } else if (error.error) {
        toast.error(error.error);
      } else {
        toast.error("Failed to upgrade plan. Please try again.");
      }
    } finally {
      hideLoader();
    }
  };

  const viewPortal = () => {
    getPortalLink().then((res) => {
      window.open(res.url, "_blank");
    });
  };

  const cancellationDate = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const isCancellationScheduled =
    !!organization?.cancelledInitiatedAt &&
    !organization?.subscriptionCancelled;

  const handleCancelSubscription = async () => {
    try {
      setIsCancelling(true);
      await cancelSubscription();
      toast.success(
        "Cancellation scheduled. Your subscription will remain active until the end of the billing period.",
      );
      setShowCancelConfirm(false);
      if (getProfile) await getProfile();
    } catch (err) {
      toast.error(err?.message || "Failed to cancel subscription");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsReactivating(true);
      const result = await reactivateSubscription();
      if (result?.requiresNewSubscription) {
        toast.success(
          "Cancellation cleared. Your subscription will continue until the end of the billing period.",
        );
      } else {
        toast.success("Subscription reactivated successfully.");
      }
      if (getProfile) await getProfile();
    } catch (err) {
      toast.error(err?.message || "Failed to reactivate subscription");
    } finally {
      setIsReactivating(false);
    }
  };

  // Helper function to format date
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  // Helper function to format currency
  const formatCurrency = (amount, currencyCode = null) => {
    const curr = currencyCode || currency.toLowerCase();
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: curr.toUpperCase(),
    }).format(amount / 100);
  };

  // Helper function to get price based on currency and billing cycle
  const getPrice = (plan, subscriptionType) => {
    if (!plan || plan.contact) return null;
    const isMonthly = subscriptionType?.toLowerCase() === "monthly";
    if (isINR) {
      return isMonthly ? plan.priceMonthlyIN : plan.priceAnnualIN;
    }
    return isMonthly ? plan.priceMonthly : plan.priceAnnual;
  };

  // Helper function to format price display
  const formatPrice = (plan, subscriptionType) => {
    const price = getPrice(plan, subscriptionType);
    if (price === null || price === undefined) return "N/A";
    return `${currencySymbol}${price.toLocaleString("en-US")}`;
  };

  // Helper function to get plan display name from planId
  const getPlanDisplayName = (planId) => {
    if (!planId) return "N/A";

    const planNameMap = {
      FREE: "Free",
      FREE_IN: "Free",
      STANDARD: "Standard",
      BUSINESS: "Business",
      BUSINESS_PRO: "Business Pro",
      GROUP_STARTER: "Group Starter",
      GROUP_PRO: "Group Pro",
      ENTERPRISE_HOLDING: "Enterprise Holding",
      STANDARD_IN: "Standard",
      BUSINESS_IN: "Business",
      BUSINESS_PRO_IN: "Business Pro",
      GROUP_STARTER_IN: "Group Starter",
      GROUP_PRO_IN: "Group Pro",
      ENTERPRISE_HOLDING_IN: "Enterprise Holding",
    };

    return planNameMap[planId] || planId;
  };

  // Helper function to get trial status
  const getTrialStatus = () => {
    if (!subscription) return null;

    if (subscription.isTrialing) {
      const trialEndDate = new Date(subscription.trialEnd * 1000);
      const now = new Date();
      const daysLeft = Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24));

      return {
        status: "trialing",
        daysLeft: Math.max(0, daysLeft),
        endDate: formatDate(subscription.trialEnd),
      };
    }

    return {
      status: subscription.status,
      daysLeft: null,
      endDate: null,
    };
  };

  // Helper function to format usage display
  const formatUsage = (used, allowed) => {
    if (allowed < 0) {
      return "Unlimited";
    }
    return `${used} / ${allowed}`;
  };

  // Helper function to get usage percentage
  const getUsagePercentage = (used, allowed) => {
    if (allowed === 0) return 0;
    return Math.round((used / allowed) * 100);
  };

  const trialStatus = getTrialStatus();

  const sidebarLinks = [
    { value: SUB_TABS.overview, label: "Overview", icon: CreditCardIcon },
    { value: SUB_TABS.usage, label: "Usage", icon: ChartBarIcon },
    ...(isOrgOwner
      ? [
          {
            value: SUB_TABS.upgrade,
            label: "Upgrade",
            icon: ArrowTrendingUpIcon,
          },
        ]
      : []),
    { value: SUB_TABS.billing, label: "Billing", icon: BanknotesIcon },
    { value: SUB_TABS.activity, label: "Activity", icon: ClockIcon },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-screen">
      {/* ── Vertical sidebar: Navigation links ── */}
      <aside className="w-full lg:w-60 shrink-0 lg:border-r px-4 py-4 border-gray-200 lg:rounded-none h-screen">
        <nav className="space-y-1">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            const isActive = activeSubTab === link.value;
            return (
              <button
                key={link.value}
                type="button"
                onClick={() => setActiveSubTab(link.value)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {link.label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 min-w-0 p-4">
        {activeSubTab === SUB_TABS.overview && (
          <SubscriptionOverviewTab
            organization={organization}
            isOrgOwner={isOrgOwner}
            availablePlans={availablePlans}
            getPlanDisplayName={getPlanDisplayName}
            formatUsage={formatUsage}
            currencySymbol={currencySymbol}
            setActiveSubTab={setActiveSubTab}
            SUB_TABS={SUB_TABS}
          />
        )}

        {activeSubTab === SUB_TABS.upgrade && isOrgOwner && (
          <SubscriptionUpgradeTab
            organization={organization}
            availablePlans={availablePlans}
            loadingPlans={loadingPlans}
            handlePlanSelection={handlePlanSelection}
            handleUpgradeClick={handleUpgradeClick}
            isLoading={isLoading}
            formatPrice={formatPrice}
            onContactSale={(plan) => {
              setSelectedPlanForContactSale(plan);
              setShowContactSaleModal(true);
            }}
          />
        )}

        {activeSubTab === SUB_TABS.usage && (
          <SubscriptionUsageTab
            organization={organization}
            getPlanDisplayName={getPlanDisplayName}
            formatUsage={formatUsage}
            getUsagePercentage={getUsagePercentage}
            isOrgOwner={isOrgOwner}
            setActiveSubTab={setActiveSubTab}
            SUB_TABS={SUB_TABS}
          />
        )}

        {activeSubTab === SUB_TABS.billing && (
          <SubscriptionBillingTab
            isOrgOwner={isOrgOwner}
            organization={organization}
            user={user}
            subscriptionDetails={subscriptionDetails}
            loadingDetails={loadingDetails}
            paymentCountry={paymentCountry}
            viewPortal={viewPortal}
            cancellationDate={cancellationDate}
            isCancellationScheduled={isCancellationScheduled}
            onCancelSubscription={handleCancelSubscription}
            onReactivateSubscription={handleReactivateSubscription}
            isCancelling={isCancelling}
            isReactivating={isReactivating}
            onShowCancelConfirm={() => setShowCancelConfirm(true)}
            isINR={isINR}
          />
        )}

        {activeSubTab === SUB_TABS.activity && <SubscriptionActivityTab />}
      </div>

      {/* Cancel Subscription Confirmation Modal (INR) */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Cancel Subscription?
              </h3>
            </div>
            <p className="text-gray-600 mb-2">
              Your subscription will remain active until the end of the current
              billing period
              {cancellationDate ? (
                <span className="font-medium text-gray-800">
                  {" "}
                  ({cancellationDate})
                </span>
              ) : null}
              . After that, you will lose access to premium features.
            </p>
            <p className="text-sm text-gray-400 mb-6">
              This action schedules a cancellation. You can still use all
              features until the period ends.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 border border-gray-300 text-gray-700 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Keep My Plan
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                className="flex-1 bg-red-600 text-white px-4 py-3 rounded-xl text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {isCancelling ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Confirmation Modal */}
      <UpgradeConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleUpgradeConfirm}
        plan={selectedPlan?.plan}
        billingCycle={selectedPlan?.interval}
        isLoading={isLoading}
        isINR={isINR}
      />

      {/* Contact Sale Modal for Business Pro */}
      <ContactSaleModal
        isOpen={showContactSaleModal}
        onClose={() => {
          setShowContactSaleModal(false);
          setSelectedPlanForContactSale(null);
        }}
        plan={selectedPlanForContactSale}
        user={user}
      />
    </div>
  );
};

export default SubscriptionAndUsage;
